import React, { useState } from 'react';
import { Package, ShoppingCart, Mail, Info } from 'lucide-react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Badge from '../../components/common/Badge';
import StatusBadge from '../../components/common/StatusBadge';
import Skeleton from '../../components/common/Skeleton';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';
import Tooltip from '../../components/common/Tooltip';
import FormField from '../../components/common/FormField';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import Checkbox from '../../components/common/Checkbox';
import Radio from '../../components/common/Radio';
import MaskedInput from '../../components/common/MaskedInput';
import QuantityStepper from '../../components/common/QuantityStepper';
import SearchInput from '../../components/common/SearchInput';
import Breadcrumb from '../../components/common/Breadcrumb';
import Pagination from '../../components/common/Pagination';
import Drawer from '../../components/common/Drawer';
import Avatar from '../../components/common/Avatar';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '../../components/common/Tabs';
import OrderTimeline from '../../components/order/OrderTimeline';
import PaymentTimeline from '../../components/payment/PaymentTimeline';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-app-text mb-4 pb-2 border-b border-app-border">{title}</h2>
    <div className="flex flex-wrap gap-4 items-start">{children}</div>
  </section>
);

const mockOrder = {
  createdAt: new Date(Date.now() - 5 * 86400000),
  approvedAt: new Date(Date.now() - 4 * 86400000),
  paidAt: new Date(Date.now() - 3 * 86400000),
  shippedAt: new Date(Date.now() - 2 * 86400000),
  trackingCode: 'BR123456789',
  status: 'shipped',
};

const mockPayment = {
  createdAt: new Date(Date.now() - 10 * 86400000),
  dueDate: new Date(Date.now() + 3 * 86400000),
  status: 'pending',
};

const ComponentsPreview = () => {
  const [tab, setTab] = useState('buttons');
  const [qty, setQty] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checkbox, setCheckbox] = useState(false);
  const [radio, setRadio] = useState('a');
  const [cpf, setCpf] = useState('');
  const [selValue, setSelValue] = useState('');

  const rows = Array.from({ length: 12 }).map((_, i) => ({
    id: i + 1,
    name: `Item ${i + 1}`,
    status: ['pending', 'approved', 'paid', 'shipped', 'delivered'][i % 5],
  }));

  return (
    <div className="max-w-6xl mx-auto p-6">
      <Breadcrumb
        items={[
          { label: 'Reviter', to: '/' },
          { label: 'Dev' },
          { label: 'Componentes' },
        ]}
        className="mb-4"
      />
      <h1 className="text-3xl font-extrabold text-app-text mb-2">Design System</h1>
      <p className="text-app-text-secondary mb-8">
        Rota dev-only (<code>/dev/componentes</code>) pra verificar primitivos.
      </p>

      <Tabs value={tab} onChange={setTab} className="mb-8">
        <TabList>
          <Tab value="buttons">Buttons</Tab>
          <Tab value="inputs">Inputs</Tab>
          <Tab value="feedback">Feedback</Tab>
          <Tab value="nav">Nav</Tab>
          <Tab value="data">Data</Tab>
          <Tab value="timelines">Timelines</Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="buttons">
            <Section title="Buttons">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="accent">Accent</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="subtle">Subtle</Button>
              <Button variant="primary" loading>
                Loading
              </Button>
              <Button variant="primary" leftIcon={<Package size={16} />}>
                Com ícone
              </Button>
              <Button variant="primary" disabled>
                Disabled
              </Button>
            </Section>
            <Section title="Sizes">
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
              <Button size="xl">Extra</Button>
            </Section>
          </TabPanel>

          <TabPanel value="inputs">
            <Section title="Input / FormField">
              <div className="w-80">
                <Input label="Email" placeholder="seu@email.com" leftIcon={<Mail size={18} />} />
              </div>
              <div className="w-80">
                <Input label="Com erro" error="Campo obrigatório" />
              </div>
              <div className="w-80">
                <FormField label="FormField + Select" htmlFor="sel1">
                  <Select
                    id="sel1"
                    value={selValue}
                    onChange={(e) => setSelValue(e.target.value)}
                    placeholder="Escolha…"
                    options={[
                      { value: 'a', label: 'Opção A' },
                      { value: 'b', label: 'Opção B' },
                    ]}
                  />
                </FormField>
              </div>
              <div className="w-80">
                <Textarea label="Textarea" rows={3} placeholder="Digite algo…" autoResize />
              </div>
              <div className="w-80">
                <MaskedInput
                  label="CPF"
                  mask="cpf"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                  placeholder="000.000.000-00"
                />
              </div>
              <div className="w-80">
                <SearchInput value={search} onChange={setSearch} placeholder="Buscar produto…" />
              </div>
            </Section>
            <Section title="Checkbox / Radio / Quantity">
              <Checkbox label="Aceitar termos" checked={checkbox} onChange={(e) => setCheckbox(e.target.checked)} />
              <div className="flex gap-4">
                <Radio label="A" name="demo" value="a" checked={radio === 'a'} onChange={(e) => setRadio(e.target.value)} />
                <Radio label="B" name="demo" value="b" checked={radio === 'b'} onChange={(e) => setRadio(e.target.value)} />
              </div>
              <QuantityStepper value={qty} onChange={setQty} />
            </Section>
          </TabPanel>

          <TabPanel value="feedback">
            <Section title="Badges">
              <Badge color="primary">Primary</Badge>
              <Badge color="success" variant="solid">Sucesso</Badge>
              <Badge color="warning" variant="outline">Outline</Badge>
              <Badge color="error" dot>Com dot</Badge>
              <StatusBadge domain="order" status="awaiting_approval" />
              <StatusBadge domain="order" status="paid" />
              <StatusBadge domain="payment" status="overdue" />
              <StatusBadge domain="store" status="suspended" />
            </Section>
            <Section title="Loading">
              <Skeleton variant="text" lines={3} width={240} />
              <Skeleton variant="circle" width={48} height={48} />
              <Skeleton variant="block" width={120} height={80} />
              <Spinner size="md" />
              <Spinner size="lg" className="text-primary" />
            </Section>
            <Section title="Empty & Tooltip">
              <div className="w-full max-w-md border border-app-border rounded-lg">
                <EmptyState
                  icon={Package}
                  title="Sem produtos"
                  description="Cadastre o primeiro pra começar."
                  action={<Button size="sm">Novo produto</Button>}
                />
              </div>
              <Tooltip content="Dica útil aqui" position="top">
                <Button variant="ghost" leftIcon={<Info size={16} />}>Hover pra tooltip</Button>
              </Tooltip>
            </Section>
            <Section title="Avatar">
              <Avatar name="Fabrício Saciloto" size="xs" />
              <Avatar name="Fabrício Saciloto" size="sm" />
              <Avatar name="Fabrício Saciloto" size="md" />
              <Avatar name="Fabrício Saciloto" size="lg" />
              <Avatar name="Fabrício Saciloto" size="xl" />
            </Section>
          </TabPanel>

          <TabPanel value="nav">
            <Section title="Breadcrumb / Pagination">
              <Breadcrumb
                items={[
                  { label: 'Home', to: '/' },
                  { label: 'Pedidos', to: '/revendedor/painel/pedidos' },
                  { label: '#1234' },
                ]}
              />
              <Pagination page={page} pageSize={10} total={256} onPageChange={setPage} className="w-full" />
            </Section>
            <Section title="Drawer & ConfirmDialog">
              <Button onClick={() => setDrawerOpen(true)} leftIcon={<ShoppingCart size={16} />}>
                Abrir Drawer
              </Button>
              <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Painel lateral" size="md">
                <div className="p-4">Conteúdo do drawer vai aqui.</div>
              </Drawer>
              <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                Abrir ConfirmDialog
              </Button>
              <ConfirmDialog
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => setConfirmOpen(false)}
                title="Excluir produto?"
                message="Essa ação não pode ser desfeita."
                confirmLabel="Excluir"
                variant="danger"
              />
            </Section>
          </TabPanel>

          <TabPanel value="data">
            <Section title="DataTable">
              <div className="w-full">
                <DataTable
                  columns={[
                    { key: 'id', header: 'ID', width: 80 },
                    { key: 'name', header: 'Nome' },
                    {
                      key: 'status',
                      header: 'Status',
                      render: (r) => <StatusBadge domain="order" status={r.status} />,
                    },
                  ]}
                  rows={rows.slice((page - 1) * 5, page * 5)}
                  total={rows.length}
                  page={page}
                  pageSize={5}
                  onPageChange={setPage}
                  selectable
                  renderBulkActions={(ids) => (
                    <Button size="sm" variant="danger">
                      Excluir {ids.length}
                    </Button>
                  )}
                />
              </div>
            </Section>
          </TabPanel>

          <TabPanel value="timelines">
            <Section title="OrderTimeline">
              <div className="max-w-md">
                <OrderTimeline order={mockOrder} />
              </div>
            </Section>
            <Section title="PaymentTimeline">
              <div className="max-w-md">
                <PaymentTimeline payment={mockPayment} />
              </div>
            </Section>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default ComponentsPreview;
